/** Shared, DOM-free primitives for the app's finger-tracked horizontal
 * gestures — the tab drag-nav and the
 * swipe-to-go-back. Kept pure and in one place
 * so the axis-lock / velocity / edge logic has a single implementation the two
 * hooks share rather than each re-deriving it. */

/** Minimum horizontal travel, in px, for a released drag to commit. */
export const DRAG_MIN_DISTANCE = 50

/** Minimum velocity, in px/ms, for a released drag to commit even if it
 * didn't travel DRAG_MIN_DISTANCE — lets a fast short flick commit too. */
export const DRAG_MIN_VELOCITY = 0.5

/** Px of initial travel (in either axis) before the gesture's axis is
 * decided. Below this, the gesture is ambiguous and nothing moves yet. */
export const AXIS_LOCK_DISTANCE = 10

/** Once axis-lock distance is reached, vertical travel must stay under this
 * fraction of horizontal travel for the gesture to read as horizontal. */
export const DRAG_MAX_OFF_AXIS_RATIO = 0.5

/** Width, in px, of the left-edge zone a back-swipe must begin inside — the
 * default absolute fallback for `isWithinLeftEdge`. `useSwipeBack` instead
 * derives its zone from `EDGE_SWIPE_FRACTION` of the page width. */
export const EDGE_SWIPE_ZONE = 28

/** Fraction of the page width that counts as the back-swipe start zone. A thin
 * platform-style edge (~28px) proved too fiddly to grab, so the whole left
 * side of the page starts the gesture; vertical scrolls and in-page
 * horizontal-scrollers are still protected by the axis-lock and the exclude
 * selector, so widening the start zone is safe. */
export const EDGE_SWIPE_FRACTION = 0.5

export type DragDirection = 'left' | 'right'

/** Given the total travel and duration of a released drag, decide whether it
 * commits. Distance OR velocity clears the bar — a fast short flick commits the
 * same as a slow long drag. Pure so it's easy to unit test independent of DOM
 * touch events. */
export function resolveDragCommit(dx: number, durationMs: number): boolean {
  const velocity = durationMs > 0 ? Math.abs(dx) / durationMs : Infinity
  return Math.abs(dx) >= DRAG_MIN_DISTANCE || velocity >= DRAG_MIN_VELOCITY
}

/** Given travel so far, decide whether the gesture reads as a horizontal drag
 * or a vertical scroll, or `null` if it's too early to tell. Pure so it's easy
 * to unit test independent of DOM touch events. */
export function resolveDragAxis(
  dx: number,
  dy: number
): 'horizontal' | 'vertical' | null {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_DISTANCE) return null
  return Math.abs(dy) > Math.abs(dx) * DRAG_MAX_OFF_AXIS_RATIO
    ? 'vertical'
    : 'horizontal'
}

/** Whether a touch that started at `touchX` (viewport px) began inside the
 * left-edge zone of a container whose left edge sits at `containerLeft`. Only
 * such touches may start a back-swipe. Pure so the edge rule is unit-testable
 * independent of DOM. */
export function isWithinLeftEdge(
  touchX: number,
  containerLeft: number,
  zone: number = EDGE_SWIPE_ZONE
): boolean {
  return touchX - containerLeft <= zone
}
